import { useCallback, useMemo, useState } from "react";

// REACT QUERY
import { useQuery } from "@tanstack/react-query";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";

// MUI
import { Box, Grid, Link, Stack, Tooltip } from "@mui/material";

import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";

// API
import { getReviewDetails } from "@/DataPages/api/socialMediaReviews";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";
import { useSocialMediaReviewDetailDialog } from "@DataPages/context/customerRelations/SocialMediaReviewDetailedDialogContext";

// Components
import ReviewDetailsDialog from "../components/ReviewDetailsDialog";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Helpers
import { and } from "@Common/helpers/sonar";
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { filterByAssociates, filterByRating, filterBySearchCriteria } from "@/DataPages/helpers/socialMediaReviews/reviews";

// Constants
import { HEADER_CLASS_NAME, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";

// Assets
import WarningIcon from "@/assets/icons/common/warning-icon.svg";

// Types
import { IDialogContent, IReviewDateRange } from "@DataPages/types/socialMediaReviews/utils";
import { TSelectionItem } from "@DataPages/components/CRM/SocialMediaReviews/interfaces";

type SocialMediaReviewStoreContentProps = Readonly<{
	isLoading: boolean;
	columns: GridColDef[];
	rows: Record<string, string | number | boolean>[];
	isDataPresent: boolean;
	selectedDates: IReviewDateRange | null;
	selectedItem: TSelectionItem;
}>;

export default function SocialMediaReviewStoreTable(props: Readonly<SocialMediaReviewStoreContentProps>) {
	const { selectedDates, selectedItem } = props;
	const { commissionCycleId, startDateCommissionCycle, payPeriodEndDate } = selectedItem;

	const apiRef = useGridApiRef();
	const location = useLocation();
	const { locationId, timezone } = location ?? {};

	const dateRange = selectedDates ?? { startDate: startDateCommissionCycle, endDate: payPeriodEndDate };

	const { filter, dispatch } = useSocialMediaReviewDetailDialog();

	// Inner Constants (Do not take out)
	const source = "Store";

	// States
	const [openDialog, setOpenDialog] = useState(false);
	const [dialogContent, setDialogContent] = useState<IDialogContent | null>(null);

	/********************************************* QUERIES ************************************************/
	const { data: storeDetailsResponse, isLoading: isStoreReviewsLoading } = useQuery({
		queryKey: [
			`${source}-review-details-dialog`,
			locationId,
			dialogContent?.columnName,
			commissionCycleId,
			dateRange?.startDate,
			dateRange?.endDate,
		],
		queryFn: () =>
			getReviewDetails(locationId, commissionCycleId, {
				timezone,
				dateRange,
				siteName: dialogContent?.columnName ?? "",
				source: "store",
			}),
		enabled: and(
			Boolean(locationId),
			Boolean(commissionCycleId),
			Boolean(dialogContent?.columnName),
			Boolean(openDialog),
			Boolean(timezone)
		),
		staleTime: Infinity,
		gcTime: 5 * 60 * 1000, // 5 Min
	});

	const transformedColumns = useMemo((): GridColDef[] => {
		return props.columns?.map((column) => {
			return {
				...column,
				resizable: false,
				headerClassName: `${column.headerClassName} [&_.MuiDataGrid-columnHeaderTitle]:!text-[12px] ${HEADER_CLASS_NAME}`,
				cellClassName: getCellClassNameFn([], { noWrap: true }, { base: "!bg-primary hover:!bg-primary" }),
				renderCell: (params) => {
					const content = params.row[column.field];
					const totalReviews = content?.totalReviews;
					const neededResponseTotal = content?.neededResponseTotal;

					const noun = neededResponseTotal === 1 ? "Review" : "Reviews";
					const verb = neededResponseTotal === 1 ? "needs" : "need";

					return (
						<Stack direction={"row"} justifySelf={"self-end"} spacing={0.75} alignItems={"center"} mt={-0.25}>
							{Boolean(neededResponseTotal) && (
								<Tooltip title={`${neededResponseTotal} ${noun} ${verb} response`} arrow>
									<Box pt={0.5}>
										<img src={WarningIcon} alt="warning-icon" />
									</Box>
								</Tooltip>
							)}
							<TypographyCell fontSize="14px" component={"span"}>
								<Link
									component="button"
									style={{ textDecoration: "underline", color: "white", fontFamily: "montserrat" }}
									className={"!font-montserrat"}
									onClick={() => handleOpenDialog(totalReviews, params.field)}
								>
									{totalReviews}
								</Link>
							</TypographyCell>
						</Stack>
					);
				},
			} as GridColDef;
		});
	}, [props.columns]);

	const arrayStoreDetails = storeDetailsResponse?.reviewDetails;
	const filteredStoreDetails = useMemo(() => {
		if (!arrayStoreDetails) {
			return [];
		}

		let result = [...arrayStoreDetails];

		if (filter.ratings) {
			result = filterByRating(result, filter.ratings);
		}

		if (filter.text) {
			result = filterBySearchCriteria(result, filter.text);
		}

		if (filter.users?.length) {
			result = filterByAssociates(result, filter.users);
		}

		return result;
	}, [arrayStoreDetails, filter.ratings, filter.text, filter.users]);

	/********************************************* HANDLERS ************************************************/
	const handleOpenDialog = (content: string, columnName: string) => {
		setOpenDialog(true);
		setDialogContent({ content, columnName } as IDialogContent);
	};

	const handleCloseDialog = useCallback(() => {
		setOpenDialog(false);
		setDialogContent(null);
		dispatch({ type: "RESET" });
	}, [dispatch]);

	if (props.isLoading || !commissionCycleId) {
		return (
			<Grid container mb={5}>
				<Grid item xs={12}>
					<TableLoader content rows={2} />
				</Grid>
			</Grid>
		);
	}

	if (!props?.columns?.length) {
		return null;
	}

	if (!props.isDataPresent) {
		return (
			<Grid item xs={12}>
				<NoRowsOverlay />
			</Grid>
		);
	}

	return (
		<>
			<DataSmartTable
				apiRef={apiRef}
				columns={transformedColumns}
				rows={props?.rows || []}
				maxRows={15}
				disableHeaderBottomSeparator
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				columnVisibilityModel={{
					_id: false,
				}}
				slots={{
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					noRowsOverlay: {
						title: "No Social Media Reviews Data",
					},
				}}
				hideFooter
				customProps={{
					tableClassName: {
						"functional-table footer-table-included": true,
						...TAILWIND_FOR_DATA_SMART_TABLE,
						"[&_.MuiDataGrid-scrollbar]:hidden": true,
						"[&_.toolbar-actions]:!top-[0px]": true,
						"[&_.full-collapse-toolbar]:!h-[33px]": true,
						"[&_.MuiDataGrid-row--lastVisible_.MuiDataGrid-scrollbarFiller--borderTop]:!bg-primary": true,
						"[&_.MuiDataGrid-row--lastVisible_.MuiDataGrid-scrollbarFiller--borderTop]:!border-primary": true,
					},
					expandableRows: false,
					hide: false,
				}}
			/>

			<ReviewDetailsDialog
				open={openDialog}
				isLoading={isStoreReviewsLoading}
				dialogContent={dialogContent}
				totalReviewAmount={arrayStoreDetails?.length ?? 0}
				detailedReviews={filteredStoreDetails}
				source={source}
				attributedUsers={storeDetailsResponse?.attributedUsers ?? []}
				// Callbacks
				onClose={handleCloseDialog}
			/>
		</>
	);
}
